import React, { Suspense, useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import type { FC, ReactElement, Dispatch } from "react";

import * as Clipboard from "expo-clipboard";
import {
  Box,
  Center,
  Heading,
  VStack,
  HStack,
  useTheme,
  Button,
  Text,
  PresenceTransition,
  Spinner,
  useToast,
  useDisclose,
} from "native-base";
import { useWindowDimensions } from "react-native";
import {
  useQueryLoader,
  usePreloadedQuery,
  usePaginationFragment,
  useRelayEnvironment,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";
import { RecyclerListView, DataProvider, LayoutProvider } from "recyclerlistview";
import { fetchQuery } from "relay-runtime";
import type { Subscription } from "relay-runtime";

import AccountMutationProgressIndicators from "pianofunclub-crm/components/Buttons/AccountMutationProgressIndicators";
import AccountTableRow from "pianofunclub-crm/components/ListItems/AccountTableRow";
import CreateAccountModal from "pianofunclub-crm/components/Modals/CreateAccountModal";
import SendBulkCommunicationModal from "pianofunclub-crm/components/Modals/SendBulkCommunicationModal";
import type {
  AccountsStackNavigatorProps,
  AccountsStackRouteProps,
} from "pianofunclub-crm/navigation/AccountsNavigator";

import LoadingBlobs from "pianofunclub-shared/components/Animations/LoadingBlobs";
import Row from "pianofunclub-shared/components/Base/Row";
import type { TableData } from "pianofunclub-shared/components/Base/Row";
import TopTabBar from "pianofunclub-shared/components/Buttons/TopTabBar";
import ListEmptyBanner from "pianofunclub-shared/components/ListItems/ListEmptyBanner";
import Actionsheet from "pianofunclub-shared/components/NativeBaseExtended/Actionsheet";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import CustomFlatListSpinner from "pianofunclub-shared/components/Other/CustomFlatListSpinner";
import {
  AccountsIcon,
  AddCircleIcon,
  CopyIcon,
  RestartIcon,
  SendIcon,
} from "pianofunclub-shared/components/Other/Icons";
import SearchBar from "pianofunclub-shared/components/Other/SearchBar";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import { useData } from "pianofunclub-shared/providers/DataProvider";
import type {
  LoadAccounts_query_profiles$data,
  LoadAccounts_query_profiles$key,
  PianofunclubProfileModelAccountTypeChoices,
  PianofunclubProfileModelStatusChoices,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccounts_query_profiles.graphql";
import type { LoadAccountsPaginationQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccountsPaginationQuery.graphql";
import type { LoadAccountsQuery } from "pianofunclub-shared/relay/graphql/accounts/__generated__/LoadAccountsQuery.graphql";
import {
  load_accounts,
  load_accounts_pagination,
} from "pianofunclub-shared/relay/graphql/accounts/LoadAccounts";
import type { LoadMutationProgressQuery } from "pianofunclub-shared/relay/graphql/general/__generated__/LoadMutationProgressQuery.graphql";
import { load_mutation_progress } from "pianofunclub-shared/relay/graphql/general/LoadMutationProgress";
import { ACCOUNT_STATUS } from "pianofunclub-shared/utils/constants";
import {
  getScaledWindowDimension,
  isNumeric,
  titleCaseConverter,
} from "pianofunclub-shared/utils/converters";
import { getOrderBy, sortIconExtractor } from "pianofunclub-shared/utils/extractors";
import { useDebounceFunction } from "pianofunclub-shared/utils/hooks";
import { createReducer } from "pianofunclub-shared/utils/reducers";
import type { State, Action } from "pianofunclub-shared/utils/reducers";
import { accountsHubTableCsvConverter } from "pianofunclub-shared/utils/tableCsvConverters";

export type NavigationProps = AccountsStackNavigatorProps<"AccountsHub">;

type RouteProps = AccountsStackRouteProps<"AccountsHub">;

interface ScreenProps {
  navigation: NavigationProps;
  route: RouteProps;
}

interface ContentProps {
  accountType: string;
  dispatchState: Dispatch<Action<ReducerValues>>;
  flexArray: number[];
  navigation: NavigationProps;
  queryReference: PreloadedQuery<LoadAccountsQuery, Record<string, unknown>>;
  state: State<ReducerValues>;
}

export type ReducerValues = {
  contentIsRendered: boolean;
  copyDataToClipboard?: () => void;
  createAccountModalIsOpen: boolean;
  currentPageIndex: number;
  dataProvider: DataProvider;
  discountType?: string;
  isRefetching: boolean;
  layoutProvider?: LayoutProvider;
  orderBy?: string;
  parentConnections?: string[];
  pupilConnections?: string[];
  refreshHandler?: (orderBy?: string) => void;
  schoolId?: string;
  searchTerm: string;
  sendBulkCommunicationInProgress?: boolean;
  showSendBulkCommunicationModal: boolean;
  showSendBulkCommunicationProgressIndicator: boolean;
  staffConnections?: string[];
  status?: PianofunclubProfileModelStatusChoices;
  subscription?: Subscription;
  teacherConnections?: string[];
};

const LOAD_X_ACCOUNTS = 50;
const TABLE_BORDER_COLOR = "surface.400";
const TABLE_BORDER_WIDTH = 1;
const TABLE_BORDER_RADIUS = 10;

const AccountsHubContent: FC<ContentProps> = (props) => {
  const { accountType, dispatchState, flexArray, navigation, queryReference, state } = props;

  const data = usePreloadedQuery(load_accounts, queryReference);

  const {
    data: accountsData,
    hasNext,
    isLoadingNext,
    loadNext,
    refetch: refresh,
  } = usePaginationFragment<LoadAccountsPaginationQuery, LoadAccounts_query_profiles$key>(
    load_accounts_pagination,
    data
  );

  const toast = useToast();

  useEffect(() => {
    if (accountsData.profiles?.edges && accountsData.profiles.edges.length > 0) {
      dispatchState({
        input: "dataProvider",
        value: state.values.dataProvider.cloneWithRows([...accountsData.profiles.edges]),
      });
      // if there is no data, create a fresh DataProvider
    } else {
      dispatchState({
        input: "dataProvider",
        value: new DataProvider((r1, r2) => {
          return r1 !== r2;
        }),
      });
    }

    dispatchState({
      input: "refreshHandler",
      value: (orderBy?: string) => {
        dispatchState({ input: "contentIsRendered", value: false });
        refresh(
          {
            accountType: accountType,
            first: LOAD_X_ACCOUNTS,
            orderBy: orderBy,
            schoolId: state.values.schoolId,
            searchTerm: state.values.searchTerm !== "" ? state.values.searchTerm : undefined,
            status: state.values.status,
          },
          {
            fetchPolicy: "network-only",
          }
        );
      },
    });

    dispatchState({
      input: "copyDataToClipboard",
      value: () => {
        Clipboard.setStringAsync(
          accountsHubTableCsvConverter(accountsData.profiles?.edges ?? [], accountType)
        );
        toast.show({
          render: ({ id }: { id: string }) => (
            <ToastAlert id={id} status="success" title={"Copied Data to Clipboard"} toast={toast} />
          ),
        });
      },
    });

    switch (accountType) {
      case "PARENT":
        dispatchState({
          input: "parentConnections",
          value: accountsData.profiles?.__id ? [accountsData.profiles.__id] : undefined,
        });
        return;
      case "PUPIL":
        dispatchState({
          input: "pupilConnections",
          value: accountsData.profiles?.__id ? [accountsData.profiles.__id] : undefined,
        });
        return;
      case "TEACHER":
        dispatchState({
          input: "teacherConnections",
          value: accountsData.profiles?.__id ? [accountsData.profiles.__id] : undefined,
        });
        return;
      default:
        dispatchState({
          input: "staffConnections",
          value: accountsData.profiles?.__id ? [accountsData.profiles.__id] : undefined,
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsData]);

  const { colors } = useTheme();

  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(LOAD_X_ACCOUNTS);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  // stop refetch useEffects firing on first render
  const renderCount = useRef(0);
  const environment = useRelayEnvironment();

  const refetchAccounts = useCallback(
    (variables: {
      accountType: string;
      first: number;
      isDiscounted?: boolean;
      orderBy?: string;
      schoolId?: string;
      searchTerm?: string;
      status?: PianofunclubProfileModelStatusChoices;
    }): Subscription | undefined => {
      dispatchState({ input: "isRefetching", value: true });

      const subscription = fetchQuery<LoadAccountsQuery>(environment, load_accounts, variables, {
        fetchPolicy: "store-or-network",
      }).subscribe({
        complete: () => {
          dispatchState({ input: "isRefetching", value: false });
          refresh(variables, { fetchPolicy: "store-only" });
        },
        error: () => {
          dispatchState({ input: "isRefetching", value: false });
        },
      });
      return subscription;
    },
    [dispatchState, environment, refresh]
  );

  useEffect(() => {
    if (renderCount.current < 3) {
      renderCount.current += 1;
      return;
    }

    dispatchState({ input: "isRefetching", value: false });
    state.values.subscription?.unsubscribe();

    // fire refetch immediately when orderBy or filters change
    dispatchState({
      input: "subscription",
      value: refetchAccounts({
        accountType: accountType,
        first: LOAD_X_ACCOUNTS,
        isDiscounted: state.values.discountType
          ? state.values.discountType === "DISCOUNTED"
          : undefined,
        orderBy: state.values.orderBy,
        schoolId: state.values.schoolId,
        searchTerm: state.values.searchTerm !== "" ? state.values.searchTerm : undefined,
        status: state.values.status,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.orderBy, state.values.schoolId, state.values.status, state.values.discountType]);

  useDebounceFunction(
    () => {
      if (renderCount.current < 3) {
        renderCount.current += 1;
        return;
      }
      dispatchState({ input: "isRefetching", value: false });
      state.values.subscription?.unsubscribe();

      // remove any applied sorting when we search (sort by relevance instead)
      // and reapply default sorting if search text is blank
      const orderBy = state.values.searchTerm === "" ? "user__firstName,user__lastName" : undefined;
      dispatchState({ input: "orderBy", value: orderBy });
      navigation.setParams({ orderBy: orderBy });
      dispatchState({
        input: "subscription",
        value: refetchAccounts({
          accountType: accountType,
          first: LOAD_X_ACCOUNTS,
          isDiscounted: state.values.discountType
            ? state.values.discountType === "DISCOUNTED"
            : undefined,
          orderBy: orderBy,
          schoolId: state.values.schoolId,
          searchTerm: state.values.searchTerm !== "" ? state.values.searchTerm : undefined,
          status: state.values.status,
        }),
      });
    },
    // no delay if clearing text
    state.values.searchTerm !== "" ? 750 : 0,

    [state.values.searchTerm]
  );

  useEffect(() => {
    if (renderCount.current < 3) {
      renderCount.current += 1;
      return;
    }

    dispatchState({ input: "isRefetching", value: false });
    state.values.subscription?.unsubscribe();

    // fire refresh rather than refetch (to hide current content) when changing tab
    state.values.refreshHandler?.(state.values.orderBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.currentPageIndex]);

  const { scale, width: windowWidth } = useWindowDimensions();

  useEffect(() => {
    // this allows the RLV to resize when the window resizes
    dispatchState({
      input: "layoutProvider",
      value: new LayoutProvider(
        () => "VSEL",
        (type, dim) => {
          switch (type) {
            case "VSEL":
              dim.width = getScaledWindowDimension(windowWidth, scale);
              dim.height = 40;
              break;
            default:
              dim.width = 0;
              dim.height = 0;
          }
        }
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowWidth, scale]);

  const onPressProfile = useCallback(
    (
      profileId?: string | null,
      accountType?: PianofunclubProfileModelAccountTypeChoices | null
    ) => {
      if (
        profileId &&
        (accountType === "PARENT" ||
          accountType === "PUPIL" ||
          accountType === "TEACHER" ||
          accountType === "STAFF")
      ) {
        navigation.push("Account", {
          accountsHubConnectionIds:
            accountType === "PARENT"
              ? state.values.parentConnections
              : accountType === "PUPIL"
                ? state.values.pupilConnections
                : accountType === "TEACHER"
                  ? state.values.teacherConnections
                  : state.values.staffConnections,
          accountType: accountType,
          profileId: profileId,
        });
      }
    },
    [
      navigation,
      state.values.parentConnections,
      state.values.pupilConnections,
      state.values.staffConnections,
      state.values.teacherConnections,
    ]
  );

  const onPressSchool = useCallback(
    (schoolId?: string | null) => {
      if (schoolId) {
        navigation.navigate("SchoolsHubTab", {
          params: {
            schoolId: schoolId,
          },
          screen: "School",
        });
      }
    },
    [navigation]
  );

  const rowRenderer = useCallback(
    (
      _: unknown,
      data: NonNullable<LoadAccounts_query_profiles$data["profiles"]>["edges"][0],
      index: number
    ): ReactElement | null => {
      if (data?.node) {
        return (
          <Box borderColor={TABLE_BORDER_COLOR} borderLeftWidth={TABLE_BORDER_WIDTH}>
            <AccountTableRow
              cellProps={{
                hideTopBorder: index === 0,
                pressableTextProps: {
                  fontFamily: "Poppins-Regular",
                },
                px: 2,
                textProps: {
                  fontFamily: "Poppins-Regular",
                  fontSize: "md",
                  textAlign: "center",
                },
              }}
              currentPageIndex={state.values.currentPageIndex}
              data={data.node}
              flexArray={flexArray}
              onPressProfile={onPressProfile}
              onPressSchool={onPressSchool}
              rowHeight={10}
              tableBorderColor={TABLE_BORDER_COLOR}
              tableBorderWidth={TABLE_BORDER_WIDTH}
            />
          </Box>
        );
      } else {
        return null;
      }
    },
    [flexArray, onPressProfile, onPressSchool, state.values.currentPageIndex]
  );

  const renderFooter = useCallback(() => {
    return isLoadingNext ? (
      <CustomFlatListSpinner
        borderColor={TABLE_BORDER_COLOR}
        borderTopWidth={1}
        position="relative"
        pt="8"
        top="0"
      />
    ) : null;
  }, [isLoadingNext]);

  const renderListEmptyBanner = useCallback(() => {
    return (
      <ListEmptyBanner
        explainer={`No ${accountType.toLowerCase()}${
          accountType !== "STAFF" ? "s" : ""
        } found... Try expanding your search!`}
      >
        <Text fontSize="6xl">{"😐"}</Text>
      </ListEmptyBanner>
    );
  }, [accountType]);

  useEffect(() => {
    dispatchState({ input: "contentIsRendered", value: true });
    // unsubscribe from refetch on unmount
    return () => state.values.subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryReference]);

  if (!state.values.contentIsRendered) {
    return <LoadingBlobs>Loading Accounts...</LoadingBlobs>;
  }

  return (
    <Box flex={1} mx="30px">
      {state.values.dataProvider.getSize() > 0 && state.values.layoutProvider ? (
        <RecyclerListView
          canChangeSize
          dataProvider={state.values.dataProvider}
          layoutProvider={state.values.layoutProvider}
          onEndReached={onEndReached}
          onEndReachedThreshold={2000}
          renderAheadOffset={800}
          renderFooter={renderFooter}
          rowRenderer={rowRenderer}
          scrollViewProps={{
            contentContainerStyle: {
              borderBottomRadius: TABLE_BORDER_RADIUS,
              borderBottomWidth:
                (accountsData.profiles?.edges.length ?? 0 > 0) && !isLoadingNext
                  ? TABLE_BORDER_WIDTH
                  : 0,
              borderColor:
                // @ts-expect-error can't index with variable
                colors?.[TABLE_BORDER_COLOR.split(".")[0]][TABLE_BORDER_COLOR.split(".")[1]],
              marginBottom: 40,
              overflow: "hidden",
            },
            showsVerticalScrollIndicator: false,
          }}
          style={{
            flex: 1,
            minHeight: 1,
          }}
          suppressBoundedSizeException
          useWindowScroll
        />
      ) : (
        renderListEmptyBanner()
      )}
    </Box>
  );
};

const AccountsHubScreen: FC<ScreenProps> = (props) => {
  const { navigation, route } = props;

  const { user } = useAuth();

  const initialState = useMemo(() => {
    let currentPageIndex = route.params?.currentPageIndex
      ? Number(route.params?.currentPageIndex)
      : undefined;
    if (currentPageIndex === undefined) {
      navigation.setParams({ currentPageIndex: 0 });
      currentPageIndex = 0;
    }
    let orderBy = route.params?.orderBy;
    if (orderBy === undefined) {
      navigation.setParams({ orderBy: "user__firstName,user__lastName" });
      orderBy = "user__firstName,user__lastName";
    }
    return {
      values: {
        contentIsRendered: false,
        createAccountModalIsOpen: false,
        currentPageIndex: Number(currentPageIndex),
        dataProvider: new DataProvider((r1, r2) => {
          return r1 !== r2;
        }),
        discountType: route.params?.discountType,
        isRefetching: false,
        layoutProvider: undefined,
        orderBy: orderBy,
        parentConnections: undefined,
        pupilConnections: undefined,
        refreshHandler: undefined,
        schoolId: route.params?.schoolId,
        searchTerm: route.params?.searchTerm ?? "",
        showSendBulkCommunicationModal: false,
        showSendBulkCommunicationProgressIndicator: false,
        staffConnections: undefined,
        status: route.params?.status as PianofunclubProfileModelStatusChoices | undefined,
        subscription: undefined,
        teacherConnections: undefined,
      },
    };
  }, [
    navigation,
    route.params?.currentPageIndex,
    route.params?.discountType,
    route.params?.orderBy,
    route.params?.schoolId,
    route.params?.searchTerm,
    route.params?.status,
  ]);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const accountType = useMemo(() => {
    switch (state.values.currentPageIndex.toString()) {
      case "0":
        return "PARENT";
      case "1":
        return "PUPIL";
      case "2":
        return "TEACHER";
      default:
        return "STAFF";
    }
  }, [state.values.currentPageIndex]);

  const [loadAccountsQueryReference, loadAccountsQuery] =
    useQueryLoader<LoadAccountsQuery>(load_accounts);

  useEffect(() => {
    if (!state.values.contentIsRendered) {
      loadAccountsQuery(
        {
          accountType: accountType,
          first: LOAD_X_ACCOUNTS,
          isDiscounted: state.values.discountType
            ? state.values.discountType === "DISCOUNTED"
            : undefined,
          orderBy: state.values.orderBy,
          schoolId: state.values.schoolId,
          searchTerm: state.values.searchTerm !== "" ? state.values.searchTerm : undefined,
          status: state.values.status,
        },
        { fetchPolicy: "store-or-network" }
      );
    }
  }, [
    accountType,
    loadAccountsQuery,
    state.values.contentIsRendered,
    state.values.discountType,
    state.values.orderBy,
    state.values.schoolId,
    state.values.searchTerm,
    state.values.status,
  ]);

  const { dataState } = useData();

  const schools = useMemo(() => {
    return dataState.values.schools?.edges ?? [];
  }, [dataState.values.schools?.edges]);

  const [loadMutationProgressQueryReference, loadMutationProgressQuery] =
    useQueryLoader<LoadMutationProgressQuery>(load_mutation_progress);

  useEffect(() => {
    if (user?.profile?.id) {
      loadMutationProgressQuery(
        {
          block: 1,
          profileId: user.profile.id,
          skipInvoiceTotals: true,
          // TODO (FUTURE): not actually used but required by GraphQL schema - could be cleaned up
          startingYear: 2022,
        },
        { fetchPolicy: "network-only" }
      );
    }
  }, [loadMutationProgressQuery, user?.profile?.id]);

  const inputChangeHandler = useCallback(
    (_: unknown, inputValue?: string) => {
      // if it's a numeric input, clean it so that we match phone numbers
      // if there are spaces in the number and so that we match both 07 and +44
      let parsedInputValue = inputValue;
      if (parsedInputValue?.startsWith("+44")) {
        parsedInputValue = parsedInputValue.replace("+44", "0");
      }
      if (isNumeric(parsedInputValue?.replace(" ", ""))) {
        parsedInputValue = parsedInputValue?.replace(" ", "");
      }
      dispatchState({
        input: "searchTerm",
        value: parsedInputValue?.trim(),
      });
      navigation.setParams({
        searchTerm: parsedInputValue?.trim(),
      });
    },
    [navigation]
  );

  const inputClearHandler = useCallback(() => {
    if (state.values.searchTerm !== "") {
      dispatchState({
        input: "searchTerm",
        value: "",
      });
    }
  }, [state.values.searchTerm]);

  const sortHandler = useCallback(
    (column: string) => {
      const orderBy = getOrderBy(column, state.values.orderBy);
      dispatchState({
        input: "orderBy",
        value: orderBy,
      });
      navigation.setParams({
        orderBy: orderBy,
      });
    },
    [navigation, state.values.orderBy]
  );

  const tableHeaders = useMemo(() => {
    const orderBy = state.values.orderBy;
    // these headers are used by all account types
    const headers: TableData = [
      {
        data: `${state.values.currentPageIndex == 1 ? "Parent " : ""}Account #`,
        icon: sortIconExtractor("accountNumber", orderBy),
        onPress: () => sortHandler("accountNumber"),
      },
      {
        data: `${state.values.currentPageIndex == 1 ? "Pupil " : ""}Name`,
        icon: sortIconExtractor("user__firstName,user__lastName", orderBy),
        onPress: () => sortHandler("user__firstName,user__lastName"),
      },
    ];
    if (state.values.currentPageIndex !== 1) {
      headers.push(
        {
          data: "Email",
          icon: sortIconExtractor("user__email", orderBy),
          onPress: () => sortHandler("user__email"),
        },
        {
          data: "Phone #",
          icon: sortIconExtractor("-hasInvalidPhoneNumber,phoneNumber", orderBy),
          onPress: () => sortHandler("-hasInvalidPhoneNumber,phoneNumber"),
        }
      );
    }
    switch (state.values.currentPageIndex) {
      // parents
      case 0:
        return headers.concat([
          {
            data: "Status",
            icon: sortIconExtractor("status", orderBy),
            onPress: () => sortHandler("status"),
          },
        ]);
      // pupils
      case 1:
        return headers.concat([
          {
            data: "Parent Name",
            icon: sortIconExtractor(
              "primaryParent__user__firstName,primaryParent__user__lastName",
              orderBy
            ),
            onPress: () =>
              sortHandler("primaryParent__user__firstName,primaryParent__user__lastName"),
          },
          {
            data: "School",
            icon: sortIconExtractor("school__name", orderBy),
            onPress: () => sortHandler("school__name"),
          },
          {
            data: "School Year",
            icon: sortIconExtractor("schoolYear", orderBy),
            onPress: () => sortHandler("schoolYear"),
          },
          {
            data: "Discount",
            icon: sortIconExtractor("discountCategory", orderBy),
            onPress: () => sortHandler("discountCategory"),
          },
          {
            data: "Status",
            icon: sortIconExtractor("status", orderBy),
            onPress: () => sortHandler("status"),
          },
        ]);
      case 2:
        return headers.concat([
          {
            data: "DBS Status",
            icon: sortIconExtractor("dbsExpirationDate", orderBy),
            onPress: () => sortHandler("dbsExpirationDate"),
          },
          {
            data: "Status",
            icon: sortIconExtractor("status", orderBy),
            onPress: () => sortHandler("status"),
          },
        ]);
      default:
        return headers.concat([
          {
            data: "Admin Privileges ",
          },
        ]);
    }
  }, [sortHandler, state.values.currentPageIndex, state.values.orderBy]);

  const flexArray = useMemo(() => {
    switch (state.values.currentPageIndex) {
      // parents
      case 0:
        return [1, 1.5, 2, 1.5, 1];
      // pupils
      case 1:
        return [1, 2, 2, 1.5, 1, 1, 1];
      // teachers
      case 2:
        return [1, 1.5, 2, 1.5, 1.5, 1];
      // staff
      default:
        return [1, 1.5, 2, 1.5, 1];
    }
  }, [state.values.currentPageIndex]);

  const {
    isOpen: accountActionsActionsheetIsOpen,
    onClose: accountActionsActionsheetOnClose,
    onOpen: accountActionsActionsheetOnOpen,
  } = useDisclose();

  const renderHeader = useMemo(() => {
    return (
      <VStack space="6">
        <HStack mb="4" ml="1" space="5">
          <AccountsIcon color="primary.600" size="xl" />
          <Heading color="primary.600" fontSize="xl">
            Accounts
          </Heading>
        </HStack>
        <TopTabBar
          bg="primary.100"
          borderRadius="lg"
          // reset filters and ordering when changing tab
          buttonStyle={{
            _hover: { bg: "primary.200" },
            _pressed: { bg: "primary.300" },
            alignItems: "center",
            bg: "primary.50",
            pb: 2,
            pt: 2,
          }}
          currentPageIndex={state.values.currentPageIndex}
          hideBottomBar
          onChangePage={() => {
            let orderBy = state.values.orderBy;
            if (state.values.searchTerm === "") {
              orderBy = "user__firstName,user__lastName";
              dispatchState({
                input: "orderBy",
                value: orderBy,
              });
            }
            dispatchState({
              input: "status",
              value: undefined,
            });
            dispatchState({
              input: "schoolId",
              value: undefined,
            });
            navigation.setParams({
              orderBy: orderBy,
              schoolId: undefined,
              status: undefined,
            });
          }}
          overflow="hidden"
          pages={["Parents", "Pupils", "Teachers", "Staff"]}
          selectedButtonStyle={{ bg: "primary.100" }}
          selectedTextStyle={{ color: "primary.600" }}
          setCurrentPageIndex={(index) => {
            dispatchState({
              input: "currentPageIndex",
              value: index,
            });
            navigation.setParams({ currentPageIndex: index });
          }}
          shadow={1}
          textStyle={{ color: "primary.800" }}
          width="100%"
        />
        <HStack justifyContent="space-between">
          <HStack alignItems="center" space="4">
            <Box width="350px">
              <SearchBar
                inputClearHandler={inputClearHandler}
                inputOnChangeHandler={inputChangeHandler}
                inputSearchHandler={inputChangeHandler}
                placeholderText={`Search for a ${accountType.toLowerCase()}`}
                searchText={state.values.searchTerm}
                showSearchIcon
              />
            </Box>
            {state.values.currentPageIndex == 1 ? (
              <Select
                borderRadius="2xl"
                color={state.values.schoolId ? "surface.900" : "muted.400"}
                fontSize="md"
                onValueChange={(itemValue) => {
                  const parsedValue = itemValue !== "ANY" ? itemValue : undefined;
                  dispatchState({
                    input: "schoolId",
                    value: parsedValue,
                  });
                  navigation.setParams({
                    schoolId: parsedValue,
                  });
                }}
                placeholder="Select school"
                py="2.5"
                selectedValue={state.values.schoolId ?? "ANY"}
                width="220px"
              >
                <Select.Item key={"ANY"} actionSheetLabel={"Any school"} value={"ANY"} />
                {schools.map((item) => {
                  if (!item?.node?.id) {
                    return null;
                  }

                  const isArchived = item.node.archived;

                  return (
                    <Select.Item
                      key={item.node.id}
                      actionSheetLabel={`${titleCaseConverter(item.node.name)} ${
                        isArchived ? "(Archived)" : ""
                      }`}
                      value={item.node.id}
                    />
                  );
                })}
              </Select>
            ) : null}
            <Select
              borderRadius="2xl"
              color={state.values.status ? "surface.900" : "muted.400"}
              fontSize="md"
              onValueChange={(itemValue) => {
                const parsedValue = itemValue !== "ANY" ? itemValue : undefined;
                dispatchState({
                  input: "status",
                  value: parsedValue,
                });
                navigation.setParams({
                  status: parsedValue,
                });
              }}
              placeholder="Select account status"
              py="2.5"
              selectedValue={state.values.status ?? "ANY"}
              width="220px"
            >
              <Select.Item key={"ANY"} actionSheetLabel={"Any status"} value={"ANY"} />
              {ACCOUNT_STATUS.map((item) => (
                <Select.Item
                  key={item.value}
                  actionSheetLabel={item.label}
                  startIcon={item.icon}
                  value={item.value}
                />
              ))}
            </Select>
            {accountType === "PUPIL" ? (
              <Select
                borderRadius="2xl"
                color={state.values.discountType ? "surface.900" : "muted.400"}
                fontSize="md"
                onValueChange={(itemValue) => {
                  const parsedValue = itemValue !== "ANY" ? itemValue : undefined;
                  dispatchState({
                    input: "discountType",
                    value: parsedValue,
                  });
                  navigation.setParams({
                    discountType: parsedValue,
                  });
                }}
                placeholder="Select discount type"
                py="2.5"
                selectedValue={state.values.discountType ?? "ANY"}
                width="150px"
              >
                <Select.Item key={"ANY"} actionSheetLabel={"Any type"} value={"ANY"} />
                <Select.Item
                  key={"DISCOUNTED"}
                  actionSheetLabel={"Discounted"}
                  value={"DISCOUNTED"}
                />
                <Select.Item
                  key={"FULL_PRICE"}
                  actionSheetLabel={"Full Price"}
                  value={"FULL_PRICE"}
                />
              </Select>
            ) : null}
            <Button
              bg="surface.400"
              colorScheme="surface"
              leftIcon={<RestartIcon size="5" />}
              onPress={() => state.values.refreshHandler?.(state.values.orderBy)}
              p="3"
            />
          </HStack>
          <HStack space="4">
            <Button
              _hover={{ bg: "primary.500" }}
              _pressed={{ bg: "primary.600" }}
              _text={{ fontSize: "17" }}
              bg="primary.400"
              leftIcon={<CopyIcon size="md" />}
              onPress={state.values.copyDataToClipboard}
              px="4"
              shadow={1}
            >
              Copy Table to Clipboard
            </Button>
            <Button
              _hover={{ bg: "primary.500" }}
              _pressed={{ bg: "primary.600" }}
              _text={{ fontSize: "17" }}
              bg="primary.400"
              leftIcon={<AccountsIcon mr="2" size="md" />}
              onPress={() => accountActionsActionsheetOnOpen()}
              px="4"
              shadow={1}
            >
              View Account Actions
            </Button>
          </HStack>
        </HStack>
      </VStack>
    );
  }, [
    state.values,
    accountType,
    inputChangeHandler,
    inputClearHandler,
    schools,
    navigation,
    accountActionsActionsheetOnOpen,
  ]);

  const renderTableHeaders = useMemo(() => {
    return (
      <Box bg="surface.100">
        <Box
          bg="primary.50"
          borderColor={TABLE_BORDER_COLOR}
          borderTopRadius={TABLE_BORDER_RADIUS}
          borderWidth={TABLE_BORDER_WIDTH}
        >
          <Row
            cellProps={{
              hideOuterSideBorder: true,
              hideTopBorder: true,
              px: 2,
              textProps: {
                fontSize: "md",
                textAlign: "center",
              },
            }}
            data={tableHeaders}
            flexArray={flexArray}
            rowHeight={10}
            rowIndex={0}
            tableBorderColor={TABLE_BORDER_COLOR}
            tableBorderWidth={TABLE_BORDER_WIDTH}
          />
        </Box>
      </Box>
    );
  }, [flexArray, tableHeaders]);

  const renderRefetchIndicator = useMemo(() => {
    return state.values.isRefetching ? <CustomFlatListSpinner top="270" /> : null;
  }, [state.values.isRefetching]);

  const accountModalKey = useRef(0);
  const bulkCommunicationModalKey = useRef(0);

  const renderAccountOptionsActionsheet = useMemo(() => {
    const showBulkCommunicationLoadingSpinner =
      state.values.sendBulkCommunicationInProgress ||
      Boolean(
        user?.profile?.profileGroup?.sendBulkCommunicationProgress != null &&
          user?.profile?.profileGroup?.sendBulkCommunicationProgress >= 0 &&
          user?.profile?.profileGroup?.sendBulkCommunicationProgress < 100
      );

    return (
      <Actionsheet
        animationType="fade"
        hideDragIndicator
        isOpen={accountActionsActionsheetIsOpen}
        justifyContent="center"
        onClose={accountActionsActionsheetOnClose}
        size="lg"
      >
        <Actionsheet.Content
          alignItems="center"
          closeButtonProps={{ right: "5", top: "5" }}
          justifyContent="center"
          mx="auto"
          roundedBottom={12}
          roundedTop={12}
          showCloseButton
          w="40%"
        >
          <Text alignSelf="center" fontSize="xl" fontWeight="bold" py="4">
            Account Actions
          </Text>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            leftIcon={
              <Center size="7">
                <AddCircleIcon color="primary.500" size="6" />
              </Center>
            }
            onPress={() => {
              accountActionsActionsheetOnClose();
              dispatchState({
                input: "createAccountModalIsOpen",
                value: true,
              });
            }}
          >
            {"Create Account"}
          </Actionsheet.Item>
          <Actionsheet.Item
            _text={{ fontSize: "lg" }}
            isDisabled={showBulkCommunicationLoadingSpinner}
            leftIcon={
              <Center size="7">
                {showBulkCommunicationLoadingSpinner ? (
                  <Spinner />
                ) : (
                  <SendIcon color="primary.400" size="5" />
                )}
              </Center>
            }
            onPress={() => {
              accountActionsActionsheetOnClose();
              dispatchState({
                input: "showSendBulkCommunicationModal",
                value: true,
              });
            }}
          >
            {"Send Bulk Parent Email/SMS"}
          </Actionsheet.Item>
        </Actionsheet.Content>
      </Actionsheet>
    );
  }, [
    accountActionsActionsheetIsOpen,
    accountActionsActionsheetOnClose,
    state.values.sendBulkCommunicationInProgress,
    user?.profile?.profileGroup?.sendBulkCommunicationProgress,
  ]);

  const renderCreateAccountModal = useMemo(() => {
    // conditional render due to bug where modal eats clicks despite being closed
    return state.values.createAccountModalIsOpen ? (
      <CreateAccountModal
        // reset modal state each time it's opened
        key={accountModalKey.current}
        initialAccountType={accountType}
        parentConnections={state.values.parentConnections}
        pupilConnections={state.values.pupilConnections}
        schools={schools}
        setShowModal={(isOpen) => {
          dispatchState({
            input: "createAccountModalIsOpen",
            value: isOpen,
          });
          setTimeout(() => {
            accountModalKey.current += 1;
          }, 500);
        }}
        showModal={state.values.createAccountModalIsOpen}
        staffConnections={state.values.staffConnections}
        teacherConnections={state.values.teacherConnections}
      />
    ) : null;
  }, [
    accountType,
    schools,
    state.values.parentConnections,
    state.values.pupilConnections,
    state.values.createAccountModalIsOpen,
    state.values.staffConnections,
    state.values.teacherConnections,
  ]);

  const renderSendBulkCommunicationModal = useMemo(() => {
    return (
      <SendBulkCommunicationModal
        key={bulkCommunicationModalKey.current}
        hideModal={() => {
          dispatchState({
            input: "showSendBulkCommunicationModal",
            value: false,
          });
          // reset state on close
          setTimeout(() => {
            bulkCommunicationModalKey.current += 1;
          }, 500);
        }}
        onSendFinish={() =>
          dispatchState({
            input: "sendBulkCommunicationInProgress",
            value: false,
          })
        }
        onSendStart={() => {
          dispatchState({
            input: "showSendBulkCommunicationProgressIndicator",
            value: true,
          });
          dispatchState({
            input: "sendBulkCommunicationInProgress",
            value: true,
          });
        }}
        showModal={state.values.showSendBulkCommunicationModal}
      />
    );
  }, [state.values.showSendBulkCommunicationModal]);

  return (
    <>
      <Box bg="surface.100" flex={1} pt="70">
        <PresenceTransition
          animate={{
            opacity: 1,
            transition: {
              delay: 0,
              duration: 800,
            },
          }}
          initial={{
            opacity: 0,
          }}
          style={{ flex: 1 }}
          visible={Boolean(user?.profile?.id)}
        >
          <VStack bg="surface.100" mx="30px" pt="6" space="6">
            {renderHeader}
            {renderTableHeaders}
          </VStack>
          {loadAccountsQueryReference != null ? (
            <Suspense fallback={<LoadingBlobs>Loading Accounts...</LoadingBlobs>}>
              <AccountsHubContent
                accountType={accountType}
                dispatchState={dispatchState}
                flexArray={flexArray}
                navigation={navigation}
                queryReference={loadAccountsQueryReference}
                state={state}
              />
            </Suspense>
          ) : (
            <LoadingBlobs>Loading Accounts...</LoadingBlobs>
          )}
          {renderRefetchIndicator}
        </PresenceTransition>
        {renderAccountOptionsActionsheet}
        {renderCreateAccountModal}
        {renderSendBulkCommunicationModal}
      </Box>
      {loadMutationProgressQueryReference != null ? (
        <AccountMutationProgressIndicators
          dispatchState={dispatchState}
          loadMutationProgressQuery={loadMutationProgressQuery}
          loadMutationProgressQueryReference={loadMutationProgressQueryReference}
          state={state}
        />
      ) : null}
    </>
  );
};

export const screenOptions = {
  headerTitle: "Accounts",
};

export default AccountsHubScreen;
